// src/pages/ComparisonProfile/BatchComparisonProfile.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  ButtonGroup,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components (reusable components like SubcodonProfile)
import GeneralSettings from "../../Components/Common/GeneralSettings";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";
import BreadCrumb from "../../Components/Common/BreadCrumb";

// Page-specific Components
import GroupsManager from "./components/GroupsManager";
import BatchTranscriptList from "./components/BatchTranscriptList";
import BatchOutputOptions from "./components/BatchOutputOptions";
import BatchJobStatus from "./components/BatchJobStatus";

// API
import {
  getBatchComparsionProfileData,
  generateBatchComparisonPlot,
  getPlusStatus,
} from "../../helpers/eirna_backend_helper";

// Configuration (reusing the same config as main ComparisonProfile)
import {
  comparisonProfileConfig,
  comparisonProfileDefaults,
  comparisonProfileValidation,
} from "../../config/plotConfigs/comparisonProfileConfig";

// Hooks (reusable hooks like SubcodonProfile)
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useGroupsManager } from "./hooks/useGroupsManager";
import { useBatchSettings } from "./hooks/useBatchSettings";

// Short code handling
import {
  useBatchComparisonProfileShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";
import ActionButton from "../../Components/Common/ActionButton";

const BatchComparisonProfile = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [isBatchMode, setIsBatchMode] = useState(true);
  const [loading, setLoading] = useState(true);
  const [plusStatus, setPlusStatus] = useState(null);
  const [plotLoading, setPlotLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  // API data
  const [apiData, setApiData] = useState({
    json_files: [],
    default_tran: "",
    seq_types: [],
    studies_dict: {},
    subgroup_present: false,
  });

  // Enhanced hooks with reusable components (like SubcodonProfile)
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(comparisonProfileDefaults, comparisonProfileValidation);

  const {
    selectedSeqType,
    selectedGroup,
    setSelectedSeqType,
    setSelectedGroup,
  } = useFileSelection("riboseq");
  const {
    groups,
    addGroup,
    removeGroup,
    updateGroupName,
    updateGroupColor,
    addFilesToGroup,
    removeFilesFromGroup,
    clearGroup,
    autoFillGroups,
    autoLabelGroups,
    clearAllGroups,
    validateGroupsForComparison,
    getMasterFileDict,
  } = useGroupsManager();
  const { batchSettings, updateBatchSetting, resetBatchSettings } =
    useBatchSettings();

  // Use the Batch Comparison Profile specific short code handler
  const { userPassedShortCode, autoRunPlot, setAutoRunPlot } =
    useBatchComparisonProfileShortCode({
      location,
      updateMultipleSettings,
      clearAllGroups,
      addFilesToGroup,
      updateBatchSetting,
    });

  // Handle mode toggle
  const handleModeToggle = useCallback(
    (mode) => {
      if (mode === "single") {
        // Navigate to the single mode page
        navigate(`/projects/${projectId}/comparison/`, {
          state: {
            currentSettings: settings,
            currentGroups: groups,
            selectedSeqType: selectedSeqType,
            selectedGroup: selectedGroup,
          },
        });
      } else {
        setIsBatchMode(true);
      }
    },
    [navigate, projectId, settings, groups, selectedSeqType, selectedGroup]
  );

  // Check Connect+ status before loading the rest of the page
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const plus_status_response = await getPlusStatus(projectId);
        setPlusStatus(plus_status_response.plus_status);
      } catch (err) {
        setError("Failed to check Connect+ access");
        setPlusStatus(false);
      }
    };

    if (projectId) {
      fetchStatus();
    }
  }, [projectId]);

  // Handle short code payload
  useEffect(() => {
    const shortCodePayload = location.state?.shortCodePayload;

    if (shortCodePayload && location.state?.isFromShortCode) {
      console.log(
        "Loading batch comparison settings from shared link:",
        shortCodePayload
      );

      // Map payload to settings
      updateSetting("transcript", shortCodePayload.transcript || "");
      updateSetting("minRead", shortCodePayload.minread || 20);
      updateSetting("maxRead", shortCodePayload.maxread || 150);
      updateSetting(
        "highlightStart",
        parseInt(shortCodePayload.hili_start) || 0
      );
      updateSetting("highlightStop", parseInt(shortCodePayload.hili_stop) || 0);
      updateSetting("riboSeqCoverage", shortCodePayload.cov_check || false);
      updateSetting("normalizationType", shortCodePayload.norm_type || "no");

      // Set batch-specific settings
      if (shortCodePayload.transcript_list) {
        updateBatchSetting("transcriptList", shortCodePayload.transcript_list);
      }

      toast.success("Batch comparison settings loaded from shared link");
    }
  }, [location.state, updateSetting, updateBatchSetting]);

  // Handle state from single mode navigation
  useEffect(() => {
    const navigationState = location.state;

    if (navigationState?.currentSettings && !location.state?.isFromShortCode) {
      console.log(
        "Loading settings from single mode:",
        navigationState.currentSettings
      );

      // Apply settings from single mode
      Object.entries(navigationState.currentSettings).forEach(
        ([key, value]) => {
          updateSetting(key, value);
        }
      );

      // Apply groups from single mode if available
      if (
        navigationState.currentGroups &&
        navigationState.currentGroups.length > 0
      ) {
        // Update groups logic would go here if needed
      }
    }
  }, [location.state, updateSetting]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getBatchComparsionProfileData(projectId);
        setApiData(response);

        if (response.default_tran) {
          updateSetting("transcript", response.default_tran);
        }

        const fileTypes = [
          ...new Set(response.json_files?.map((file) => file.file_type) || []),
        ];

        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }
        const uniqueGroups = [
          ...new Set(
            response.json_files?.map((file) => file.subgroup).filter(Boolean) ||
              []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }
      } catch (err) {
        setError("Failed to load project data");
        toast.error("Failed to load project data");
      } finally {
        setLoading(false);
      }
    };

    if (projectId && plusStatus === true) {
      fetchData();
    }
  }, [projectId, 
      plusStatus, 
      updateSetting, 
      setSelectedSeqType]);

  // Auto-trigger batch generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        groupsCount: groups.length,
        hasFilesInGroups: groups.some((g) => g.files.length > 0),
        csvCheck: batchSettings.csvCheck,
        htmlCheck: batchSettings.htmlCheck,
      });

      const hasFilesForComparison =
        groups.length >= 2 && groups.every((g) => g.files.length > 0);
      const hasOutputFormat = batchSettings.csvCheck || batchSettings.htmlCheck;

      if (
        !plotLoading &&
        !loading &&
        hasFilesForComparison &&
        hasOutputFormat
      ) {
        console.log(
          "All conditions met, generating batch comparison files from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGenerateBatchPlot();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [
    autoRunPlot,
    plotLoading,
    loading,
    groups.map((g) => g.files.length).join(","),
    batchSettings.csvCheck,
    batchSettings.htmlCheck,
    setAutoRunPlot,
  ]);

  // Filter files based on selected seq type and group
  const filteredFiles = useMemo(() => {
    let files = apiData.json_files || [];
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((file) => file.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((file) => file.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  // Get unique seq types and groups
  const seqTypes = useMemo(() => {
    const types = [
      ...new Set(apiData.json_files?.map((file) => file.file_type) || []),
    ];
    return types.map((type) => ({
      value: type,
      label: type,
      count:
        apiData.json_files?.filter((file) => file.file_type === type).length ||
        0,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    const groupSet = new Set(
      apiData.json_files?.map((file) => file.subgroup) || []
    );
    return Array.from(groupSet)
      .filter((group) => group)
      .map((group) => ({
        value: group,
        label: group,
        count:
          apiData.json_files?.filter((file) => file.subgroup === group)
            .length || 0,
      }));
  }, [apiData.json_files]);

  // Time estimate calculation
  const timeEstimate = useMemo(() => {
    const transcriptCount = batchSettings.transcriptList.trim()
      ? batchSettings.transcriptList
          .split("\n")
          .map((t) => t.trim())
          .filter(Boolean).length
      : 0;
    const sampleCount = groups.reduce((sum, g) => sum + g.files.length, 0);
    const hasCsv = batchSettings.csvCheck;
    const hasHtml = batchSettings.htmlCheck;

    if (transcriptCount === 0 || sampleCount === 0 || (!hasCsv && !hasHtml)) {
      return null;
    }

    let totalSeconds = 0;
    if (hasCsv) {
      const csvBase = 0.001;
      const csvPerSample = 0.002;
      totalSeconds += (csvBase + csvPerSample * sampleCount) * transcriptCount;
    }
    if (hasHtml) {
      const htmlBase = 0.3;
      const htmlPerSample = 0.05;
      totalSeconds += (htmlBase + htmlPerSample * sampleCount) * transcriptCount;
    }

    const minutes = Math.ceil(totalSeconds / 60);
    return { minutes, transcriptCount, sampleCount, hasCsv, hasHtml };
  }, [
    batchSettings.transcriptList,
    batchSettings.csvCheck,
    batchSettings.htmlCheck,
    groups,
  ]);

  // Handle generate batch plot
  const handleGenerateBatchPlot = useCallback(async () => {
    try {
      // Validate general settings
      const validation = validateSettings();
      if (!validation.isValid) {
        toast.error(validation.errors[0]);
        return;
      }

      // Validate groups for comparison
      const groupValidation = validateGroupsForComparison();
      if (!groupValidation.isValid) {
        toast.error(groupValidation.errors[0]);
        return;
      }

      //   Check if transcript list is provided
      if (!batchSettings.transcriptList.trim()) {
        toast.error(
          "Please provide at least one transcript for batch processing"
        );
        return;
      }

      // Check if at least one output format is selected
      if (!batchSettings.csvCheck && !batchSettings.htmlCheck) {
        toast.error("Please select at least one output format (CSV or HTML)");
        return;
      }

      setPlotLoading(true);
      setError(null);
      setJobStatus(null);

      // Construct master_file_dict from groups
      const masterFileDict = getMasterFileDict();

      // Use the Batch Comparison Profile specific payload creator
      const plotRequest = createPlotSpecificPayload.batchComparisonProfile({
        settings,
        projectId,
        userPassedShortCode,
        masterFileDict,
        batchSettings,
      });

      console.log("Batch comparison plot request:", plotRequest);

      const response = await generateBatchComparisonPlot(plotRequest);

      if (response.message) {
        if (response.message === "user logged out") {
          navigate("/login");
          return;
        }
        setError(response.message);
        toast.error(response.message);
        setPlotLoading(false);
      } else if (response.task_id) {
        setJobStatus({
          taskId: response.task_id,
          status: "running",
          message: `Job started with ID: ${response.task_id}`,
        });
        toast.success("Batch comparison job started successfully");
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        "Failed to generate batch comparison plot";
      setError(errorMsg);
      toast.error(errorMsg);
      setPlotLoading(false);
    }
  }, [
    settings,
    validateSettings,
    validateGroupsForComparison,
    getMasterFileDict,
    projectId,
    batchSettings,
    navigate,
  ]);

  if (plusStatus === null || (plusStatus === true && loading)) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (error && !jobStatus) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Batch Comparison Profile" },
            ]}
          />
          <Alert color="danger" className="m-4">
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </Alert>
        </Container>
      </div>
    );
  }

  if (plusStatus === false) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="info" className="mb-0">
            This plot is part of Connect+ To discuss access please contact info@eirnabio.com
          </Alert>
        </Container>
      </div>
    );
  }

  document.title = "Batch Comparison Profile | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Batch Comparison Profile" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Batch Comparison Profile
                </h4>
                <ButtonGroup>
                  <Button
                    color={!isBatchMode ? "success" : "light"}
                    onClick={() => handleModeToggle("single")}
                    style={{
                      backgroundColor: !isBatchMode ? "#1a6d51" : "#f8f9fa",
                      borderColor: !isBatchMode ? "#1a6d51" : "#d1d5db",
                      color: !isBatchMode ? "white" : "#6b7280",
                      fontWeight: "500",
                    }}
                  >
                    <i className="ri-user-line me-1"></i>
                    Single Mode
                  </Button>
                  <Button
                    color={isBatchMode ? "success" : "light"}
                    onClick={() => handleModeToggle("batch")}
                    style={{
                      backgroundColor: isBatchMode ? "#1a6d51" : "#f8f9fa",
                      borderColor: isBatchMode ? "#1a6d51" : "#d1d5db",
                      color: isBatchMode ? "white" : "#6b7280",
                      fontWeight: "500",
                    }}
                  >
                    <i className="ri-stack-line me-1"></i>
                    Batch Mode
                  </Button>
                </ButtonGroup>
              </div>
            </Col>
          </Row>

          {/* Top Row - General Settings and Seq Types/Groups */}
          <Row className="mb-4">
            <Col lg={12}>
              <GeneralSettings
                settings={settings}
                onUpdateSetting={updateSetting}
                onReset={resetSettings}
                config={comparisonProfileConfig}
                title="General Settings"
                showReset={true}
                loading={loading}
                mode="batch"
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={availableGroups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                // Configuration
                showSelectAllCheckboxes={false}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Groups Manager */}
          <Row className="mb-4">
            <Col lg={12}>
              <GroupsManager
                groups={groups}
                allFiles={apiData.json_files}
                filteredFiles={filteredFiles}
                onAddGroup={addGroup}
                onRemoveGroup={removeGroup}
                onUpdateGroupName={updateGroupName}
                onUpdateGroupColor={updateGroupColor}
                onAddFilesToGroup={addFilesToGroup}
                onRemoveFilesFromGroup={removeFilesFromGroup}
                onClearGroup={clearGroup}
                onAutoFill={() => autoFillGroups(filteredFiles)}
                onAutoLabel={() => autoLabelGroups(filteredFiles)}
                onClearAll={clearAllGroups}
              />
            </Col>
          </Row>

          {/* Batch-specific components */}
          <Row className="mb-4">
            <Col lg={6}>
              <BatchTranscriptList
                transcriptList={batchSettings.transcriptList}
                onUpdateTranscriptList={(value) =>
                  updateBatchSetting("transcriptList", value)
                }
              />
            </Col>
            <Col lg={6}>
              <BatchOutputOptions
                csvCheck={batchSettings.csvCheck}
                htmlCheck={batchSettings.htmlCheck}
                onUpdateCsvCheck={(value) =>
                  updateBatchSetting("csvCheck", value)
                }
                onUpdateHtmlCheck={(value) =>
                  updateBatchSetting("htmlCheck", value)
                }
              />
            </Col>
          </Row>

          {/* Time Estimate */}
          {timeEstimate && !jobStatus && (
            <Row className="mb-3">
              <Col lg={12}>
                <div
                  style={{
                    backgroundColor: "#f0faf5",
                    border: "1px solid #a7d7c0",
                    borderRadius: "8px",
                    padding: "14px 20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <i
                    className="ri-time-line"
                    style={{ fontSize: "20px", color: "#1a6d51", flexShrink: 0 }}
                  ></i>
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#1a6d51",
                        fontSize: "14px",
                      }}
                    >
                      Estimated job time:&nbsp;
                    </span>
                    <span
                      style={{
                        fontWeight: "700",
                        color: "#1f2937",
                        fontSize: "14px",
                      }}
                    >
                      {timeEstimate.minutes === 1
                        ? "~1 minute"
                        : `~${timeEstimate.minutes} minutes`}
                    </span>
                    <span
                      style={{
                        color: "#6b7280",
                        fontSize: "13px",
                        marginLeft: "10px",
                      }}
                    >
                      ({timeEstimate.transcriptCount}{" "}
                      {timeEstimate.transcriptCount === 1
                        ? "transcript"
                        : "transcripts"}
                      , {timeEstimate.sampleCount}{" "}
                      {timeEstimate.sampleCount === 1 ? "sample" : "samples"}
                      {timeEstimate.hasCsv && timeEstimate.hasHtml
                        ? ", CSV + HTML"
                        : timeEstimate.hasCsv
                        ? ", CSV"
                        : ", HTML"}
                      )
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Generate Plot Button */}
          <Row className="mb-3">
            <Col lg={12} className="text-end">
              <ActionButton
                onClick={handleGenerateBatchPlot}
                buttonType="plot"
                customText="Generate Files"
              />
            </Col>
          </Row>

          {/* Job Status */}
          {jobStatus && (
            <Row>
              <Col lg={12}>
                <BatchJobStatus
                  jobStatus={jobStatus}
                  onUpdateJobStatus={setJobStatus}
                  projectId={projectId}
                />
              </Col>
            </Row>
          )}
          <ToastContainer />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BatchComparisonProfile;
